import React from "react";
import type { NightKey } from "../../hooks/realtime/useNightFloor";

const fmtNight = (key: NightKey) =>
  new Date(`${key}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

// Night-floor baseline control: choose whether the ambient background (nearby
// homes caught by the same cell towers) is deducted, and which NIGHTS the
// floor is measured from. The from/to selects list exactly the nights that
// have quiet-hour data, so every possible choice is data-backed (a free date
// input can't disable interior gap dates). Purely presentational — the caller
// owns fetching, the model, and the status copy.
const NightFloorControl: React.FC<{
  loading: boolean;
  nights: NightKey[]; // ascending nights with data; empty = can't model
  enabled: boolean;
  onToggle: (on: boolean) => void;
  fromNight: NightKey | null;
  toNight: NightKey | null;
  onRange: (from: NightKey, to: NightKey) => void;
  status: string | null; // model-dependent line, composed by the caller
}> = ({ loading, nights, enabled, onToggle, fromNight, toNight, onRange, status }) => {
  const hasNights = nights.length > 0;
  const first = nights[0];
  const last = nights[nights.length - 1];
  const allSelected = hasNights && fromNight === first && toNight === last;

  // Keep the range ordered whichever select the user moved.
  const pick = (a: NightKey, b: NightKey) => (a <= b ? onRange(a, b) : onRange(b, a));

  return (
    <div className="mb-6 rounded-2xl border border-paper-300/70 bg-surface px-5 py-4 shadow-soft">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-ink">Baseline</p>
          <p className="text-xs text-ink-muted">
            Deduct the ambient background (nearby homes caught by the same towers).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-paper-300 p-0.5">
            {([false, true] as const).map((on) => (
              <button
                key={String(on)}
                onClick={() => onToggle(on)}
                disabled={on && !hasNights}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors disabled:opacity-40 ${
                  enabled === on ? "bg-brand-500 text-on-brand" : "text-ink-muted hover:bg-paper-100"
                }`}
              >
                {on ? "Night floor" : "Raw"}
              </button>
            ))}
          </div>
          {hasNights && (
            <div className="flex items-center gap-1.5 text-xs text-ink-muted">
              <span>nights</span>
              <select
                value={fromNight ?? first}
                onChange={(e) => pick(e.target.value, toNight ?? last)}
                className="rounded border border-paper-300 bg-surface px-2 py-1 text-xs text-ink"
              >
                {nights.map((n) => (
                  <option key={n} value={n}>
                    {fmtNight(n)}
                  </option>
                ))}
              </select>
              <span>→</span>
              <select
                value={toNight ?? last}
                onChange={(e) => pick(fromNight ?? first, e.target.value)}
                className="rounded border border-paper-300 bg-surface px-2 py-1 text-xs text-ink"
              >
                {nights.map((n) => (
                  <option key={n} value={n}>
                    {fmtNight(n)}
                  </option>
                ))}
              </select>
              {!allSelected && (
                <button
                  onClick={() => onRange(first, last)}
                  className="rounded-md border border-paper-300 px-2 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-paper-100"
                >
                  All nights
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <p className="mt-2 text-xs text-ink-faint">
        {loading
          ? "Measuring the night floor…"
          : !hasNights
          ? "No night-time (02–05h) data collected yet — showing raw counts."
          : status}
      </p>
    </div>
  );
};

export default NightFloorControl;
